import _ from 'lodash';

import registry from 'react-mui-lib/registry';

import ClientsideGridController from 'react-mui-lib/grid-clientside/controller';
import ClientsideGridReducer from 'react-mui-lib/grid-clientside/reducer';
import modes from 'react-mui-lib/grid-clientside/modes';


class Reducer extends ClientsideGridReducer {
    INITIAL() {
        return Object.assign(super.INITIAL(), {
            all_rows: [],
            by_id: {}
        });
    }

    LOAD_SUCCESS(state, action) {
        return this._process(Object.assign({}, state, {
            mode: modes.DATA,
            all_rows: action.data.data,
            rows: action.data.data,
            total_rows: action.data.count,
            current_page: 1
        }));
    }

    SET_FILTER(state, action) {
        const filter = action.operation === undefined ? action.value : [action.value, action.operation];
        const target = action.static ? 'static_filters' : 'filters';
        const newState = Object.assign({}, state);
        newState[target] = Object.assign({}, state[target], { [action.key]: filter });
        return this._process(newState);
    }

    CLEAR_FILTER(state, action) {
        const filters = Object.assign({}, state.filters);
        delete filters[action.key];
        return this._process(Object.assign({}, state, { filters }));
    }

    SET_SEARCH(state, action) {
        return this._process(Object.assign({}, state, {
            search: action.value
        }));
    }

    SET_PAGE(state, action) {
        const newState = Object.assign({}, state, { current_page: action.page_n });
        return this._process(newState);
    }

    SET_ORDER(state, action) {
        const newState = Object.assign({}, state, {
            order_col: action.column,
            order_dir: action.direction
        });
        return this._process(newState);
    }

    UPDATE_STATE(state, action) {
        const newState = action.update_fn(state);
        return this._process(newState);
    }

    _process(state) {
        let allRows = state.all_rows && state.all_rows.length > 0
            ? state.all_rows.slice()
            : (state.rows ? state.rows.slice() : []);

        // Merge any updated rows from state.rows (e.g. from UPDATE_STATE)
        if (state.rows && state.rows !== allRows && state.rows.length > 0) {
            const byId = {};
            allRows.forEach(r => { if (r && r.app_user_id) byId[r.app_user_id] = r; });
            state.rows.forEach(r => { if (r && r.app_user_id) byId[r.app_user_id] = r; });
            allRows = Object.values(byId);
        }

        const roundMode = allRows.length > 0 && allRows[0].game ? allRows[0].game.round_mode : null;

        const completedTimes = allRows
            .filter(r => r.completed)
            .map(r => r.completed)
            .sort();

        let nWinners = parseInt(allRows.length > 0 && allRows[0].game ? allRows[0].game.n_winners : 0, 10);
        if (isNaN(nWinners) || nWinners <= 0) nWinners = completedTimes.length;

        if (roundMode === 'SELECTABLE') {
            allRows.forEach(r => { r.is_winner_for_display = !!r.is_winner; });
        } else {
            const thresholdIndex = Math.min(nWinners - 1, completedTimes.length - 1);
            const threshold = completedTimes.length > 0 && thresholdIndex >= 0
                ? completedTimes[thresholdIndex]
                : null;
            allRows.forEach(r => {
                r.is_winner_for_display = !!r.is_winner ||
                    (r.completed && threshold && r.completed <= threshold);
            });
        }

        let filteredRows = allRows;

        if (state.filters) {
            Object.keys(state.filters).forEach(key => {
                if (key === 'is_winner') {
                    let val = state.filters[key];
                    if (Array.isArray(val)) val = val[0];
                    filteredRows = filteredRows.filter(r => r.is_winner_for_display === val);
                } else {
                    let val = state.filters[key];
                    if (Array.isArray(val)) val = val[0];
                    if (val !== undefined && val !== '') {
                        filteredRows = filteredRows.filter(r => _.get(r, key) === val);
                    }
                }
            });
        }

        if (state.search) {
            const q = ('' + state.search).toLowerCase();
            filteredRows = filteredRows.filter(r => {
                const phone = r.app_user_id || '';
                const nickname = (r.app_user && r.app_user.nickname) || '';
                return ('' + phone).toLowerCase().indexOf(q) !== -1 ||
                    ('' + nickname).toLowerCase().indexOf(q) !== -1;
            });
        }

        filteredRows.sort((a, b) => {
            const a_val = '' + _.get(a, state.order_col);
            const b_val = '' + _.get(b, state.order_col);
            const first = state.order_dir === 'asc' ? a_val : b_val;
            const second = state.order_dir === 'asc' ? b_val : a_val;
            return first.localeCompare(second);
        });

        filteredRows.forEach((r, i) => { r._index = i; });

        const totalPages = Math.max(1, Math.ceil(filteredRows.length / state.rows_per_page));
        let currentPage = Math.min(state.current_page || 1, totalPages);

        const page = filteredRows.slice(
            (currentPage - 1) * state.rows_per_page,
            currentPage * state.rows_per_page
        );

        const by_id = {};
        filteredRows.forEach(r => { by_id[r.app_user_id] = r; });

        return Object.assign({}, state, {
            all_rows: allRows,
            rows: filteredRows,
            page: page,
            current_page: currentPage,
            total_rows: filteredRows.length,
            by_id: by_id,
            mode: state.mode === modes.DATA_OLD ? modes.DATA : state.mode
        });
    }
}

class Controller extends ClientsideGridController {
    deleteFor(app_user_id) {
        const game_form_controller = registry.get('game_form_controller');
        const message_controller = registry.get('message_controller');
        const rest_client = registry.get('rest_client');

        rest_client.deleteById(
            this.name,
            `${app_user_id},${game_form_controller.state.entity_id}`
        ).then(json => {
            message_controller.showInfo('Участник удален');
            this.load();  // TODO ?
        }, error => {
            message_controller.showException('Ошибка удаления', error);
        });
    }

    //
    // add/remove participants (for games by invitation)
    //

    isParticipant(user_id) {
        return this.state.by_id.hasOwnProperty(user_id);
    }

    saveParticipants(ids_to_add, ids_to_remove) {
        const game_form_controller = registry.get('game_form_controller');
        const message_controller = registry.get('message_controller');
        const rest_client = registry.get('rest_client');

        return rest_client.post(
            `game/${game_form_controller.state.entity_id}/participants`, {
                add: ids_to_add,
                remove: ids_to_remove
            }
        ).then(data => {
            message_controller.showInfo('Участники сохранены');
        }, error => {
            message_controller.showException('Ошибка сохранения участников', error);
            throw error;
        });
    }

    addByPhone(phone) {
        const game_form_controller = registry.get('game_form_controller');
        const rest_client = registry.get('rest_client');

        return rest_client.post(
            `game/${game_form_controller.state.entity_id}/participants`, {
                add: [phone],
                remove: []
            }
        );
    }

    //
    // winners for user selectable games
    //

    getWinners(rows) {
        const rows_to_use = rows === undefined ? this.state.rows : rows;

        return rows_to_use.filter(row =>
            row.is_winner
        ).sort((a, b) => a.winner_order - b.winner_order);
    }

    countWinners() {
        return this.state.rows.reduce(
            (cnt, row) => row.is_winner ? cnt + 1 : cnt,
            0
        );
    }

    setWinner(row, is_winner) {
        this.updateState(state => {
            const idx = state.rows.indexOf(row);

            if (idx === -1) {
                console.error('ERROR: row not found in state!');
                return state;
            }

            const new_rows = [...state.rows];

            new_rows[idx] = Object.assign({}, row, {
                is_winner: is_winner,
                winner_order: is_winner ? this.countWinners() + 1 : 0
            });

            // recalculate orders sequentially

            if (!is_winner) {
                const winners = this.getWinners(new_rows);  // returns winners in order

                for (let i = 0; i < winners.length; ++i) {
                    const winner = winners[i];
                    const idx = state.rows.indexOf(winner);

                    if (idx === -1) {
                        console.error('ERROR: row not found in state!');
                        continue;
                    }

                    new_rows[idx]  = Object.assign({}, winner, {
                        winner_order: i + 1
                    });
                }
            }

            return _.assign({}, state, {
                rows: new_rows
            });
        });
    }

    moveWinnerUp(row) {
        this.updateState(state => {
            row.winner_order -= 1;

            for (let i = 0; i < state.rows.length; ++i) {
                const r = state.rows[i];

                if (!r.is_winner) continue;

                if (r.winner_order === row.winner_order) {
                    r.winner_order += 1;
                    break;
                }
            }

            return state;
        });
    }

    moveWinnerDown(row) {
        this.updateState(state => {
            row.winner_order += 1;

            for (let i = 0; i < state.rows.length; ++i) {
                const r = state.rows[i];

                if (!r.is_winner) continue;

                if (r.winner_order === row.winner_order) {
                    r.winner_order -= 1;
                    break;
                }
            }

            return state;
        });
    }

    saveWinners() {
        const game_form_controller = registry.get('game_form_controller');
        const message_controller = registry.get('message_controller');
        const rest_client = registry.get('rest_client');

        const winners = this.state.rows.filter(row =>
            row.is_winner
        ).map(row => (
            {
                id: row.app_user_id,
                order: row.winner_order
            }
        ));

        return rest_client.post(
            `game/${game_form_controller.state.entity_id}/winners`, {
                winners
            }
        ).then(data => {
            message_controller.showInfo('Победители сохранены');
        }, error => {
            message_controller.showException('Ошибка сохранения победителей', error);
            throw error;
        });
    }
}


export default new Controller({
    entity_name: 'participant',
    state_prefix: 'participant_grid',
    Reducer,
    rows_per_page: 15,
    default_order_col: 'completed'
});
